import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { Business, PendingActions, Verified, Block } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { StatCard } from '../../../components/StatCard';
import { MiniBarChart } from '../../../components/MiniBarChart';
import { EmptyState } from '../../../components/EmptyState';
import { clientDashboardApi } from '../../../api/clientOnboarding.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

function ChartCard({ title, data }: { title: string; data: Array<{ label: string; value: number }> }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data?.length ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function ClientOnboardingDashboardPage() {
  const { data } = useQuery({ queryKey: ['client-onboarding-dashboard'], queryFn: () => clientDashboardApi.dashboard() });
  const k = data?.kpis ?? {};
  const c = data?.charts ?? {};

  return (
    <>
      <PageHeader title="Client Onboarding Dashboard" subtitle="Applications, approvals and activation analytics across the client lifecycle." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total Applications" value={n(k.totalApplications)} icon={<Business />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#64748b" label="Draft" value={n(k.draft)} icon={<Business />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="Under Review" value={n(k.underReview)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0284c7" label="Commercial Review" value={n(k.commercialReview)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Active Clients" value={n(k.activeClients)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#dc2626" label="Rejected" value={n(k.rejected)} icon={<Block />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4}><StatCard color="#16a34a" label="Approved" value={n(k.approved)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#64748b" label="Inactive Clients" value={n(k.inactiveClients)} icon={<Block />} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#0284c7" label="In Approval" value={n((k.underReview ?? 0) + (k.commercialReview ?? 0))} icon={<PendingActions />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}><ChartCard title="Applications by Status" data={c.applicationsByStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Billing Structure" data={c.clientsByBillingStructure ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Source" data={c.applicationsBySource ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Approval Stage" data={c.approvalStage ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Approval TAT" data={c.approvalTAT ?? []} /></Grid>

        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Recently Submitted" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.recentlySubmitted.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Application</TableCell><TableCell>Company</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.recentlySubmitted.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.companyName)}</TableCell><TableCell><Chip size="small" label={String(r.status).replace(/_/g, ' ')} /></TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Pending Approvals" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.pendingApprovals.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Application</TableCell><TableCell>Company</TableCell><TableCell>Stage</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.pendingApprovals.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.companyName)}</TableCell><TableCell>{String(r.stage).replace(/_/g, ' ')}</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
